import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { api } from '../../../services/api'
import TemplatePreview from './TemplatePreview'

function ProductTemplateSelector({
  selectedTemplate,
  onSelectTemplate,
  product,
  category
}) {
  const { t } = useTranslation('ecommerce')
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = category ? { category } : {}
      const result = await api.getStoryTemplates(params)
      setTemplates(result.templates || [])
    } catch (err) {
      console.error('Failed to fetch templates:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [category])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const handleSelectTemplate = useCallback((templateId) => {
    if (onSelectTemplate) {
      onSelectTemplate(templateId)
    }
  }, [onSelectTemplate])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">
          {t('templateSelector.loading', 'Loading templates...')}
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-2">
          {t('templateSelector.error', 'Failed to load templates')}
        </div>
        <button
          onClick={fetchTemplates}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {t('templateSelector.retry', 'Try again')}
        </button>
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
        <p>{t('templateSelector.noTemplates', 'No templates available')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t('templateSelector.title', 'Select Template')}
        </h3>
        <span className="text-sm text-gray-500">
          {templates.length} {t('templateSelector.templates', 'templates')}
        </span>
      </div>

      {/* Product context */}
      {product && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          {t('templateSelector.forProduct', 'Template for:')} <strong>{product.title}</strong>
        </div>
      )}

      {/* Template grid */}
      <div role="group" aria-label="Template options" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {templates.map((template) => (
          <TemplatePreview
            key={template.id}
            template={template}
            selected={selectedTemplate === template.id}
            onSelect={handleSelectTemplate}
          />
        ))}
      </div>
    </div>
  )
}

ProductTemplateSelector.propTypes = {
  selectedTemplate: PropTypes.string,
  onSelectTemplate: PropTypes.func,
  product: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }),
  category: PropTypes.string,
}

export default ProductTemplateSelector
